/**
 * /market/ticket/:qrCode — página pública de entrada de evento.
 * Muestra datos del ticket y QR renderizado.
 * Si el ticket está redeemed, muestra "Ya utilizado".
 */
import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { dbSmGetTicketByQr, dbSmGetEvent, type SmTicket, type SmEvent } from "@/lib/db-service/events.js";
import { MarketPageSkeleton } from "@/components/market/MarketSkeletons";

const fetchTicketData = createServerFn({ method: "GET" })
  .inputValidator((qrCode: string) => qrCode.trim())
  .handler(async ({ data: qrCode }) => {
    const ticket = await dbSmGetTicketByQr(qrCode);
    if (!ticket) return null;
    const event = await dbSmGetEvent(ticket.tenantId, ticket.eventId);
    return { ticket, event };
  });

export const Route = createFileRoute("/market_/ticket/$qrCode")({
  pendingComponent: MarketPageSkeleton,
  pendingMs: 0,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  loader: async ({ params }: any) => {
    return fetchTicketData({ data: params.qrCode as string });
  },
  component: TicketPage,
  head: () => ({
    meta: [
      { title: "Tu entrada — Structura Market" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function TicketPage() {
  const data = Route.useLoaderData() as { ticket: SmTicket; event: SmEvent | null } | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { qrCode } = Route.useParams() as any;

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center p-8">
          <p className="text-4xl mb-4">❌</p>
          <h1 className="text-xl font-semibold text-slate-800 mb-2">Entrada no encontrada</h1>
          <p className="text-slate-500 text-sm">El código QR no es válido o la entrada no existe.</p>
        </div>
      </div>
    );
  }

  const { ticket, event } = data;

  const statusConfig: Record<string, { label: string; color: string; icon: string }> = {
    pending:   { label: "Pago pendiente",     color: "bg-amber-100 text-amber-800",  icon: "⏳" },
    confirmed: { label: "Confirmada",          color: "bg-green-100 text-green-800",  icon: "✅" },
    redeemed:  { label: "Ya utilizada",        color: "bg-slate-100 text-slate-600",  icon: "✔" },
    cancelled: { label: "Cancelada",           color: "bg-red-100 text-red-700",      icon: "❌" },
    refunded:  { label: "Reintegrada",         color: "bg-purple-100 text-purple-700",icon: "↩" },
  };

  const statusInfo = statusConfig[ticket.status] ?? {
    label: ticket.status, color: "bg-slate-100 text-slate-700", icon: "•",
  };

  const ticketUrl = typeof window !== "undefined"
    ? window.location.href
    : `https://structura.com.ar/market/ticket/${qrCode}`;

  const qrImgUrl = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(ticketUrl)}&size=240x240&format=png`;

  const dateStr = event?.startsAt
    ? new Date(event.startsAt instanceof Date ? event.startsAt : String(event.startsAt))
        .toLocaleString("es-AR", {
          dateStyle: "full",
          timeStyle: "short",
          timeZone: event.timezone ?? "America/Argentina/Buenos_Aires",
        })
    : null;

  const isRedeemed = ticket.status === "redeemed";

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className={`px-6 py-5 ${isRedeemed ? "bg-slate-400" : "bg-green-600"}`}>
          <p className="text-white/80 text-xs font-semibold uppercase tracking-widest mb-1">Entrada</p>
          <h1 className="text-white text-xl font-bold leading-tight">
            {event?.title ?? "Evento"}
          </h1>
        </div>

        {/* Status badge */}
        <div className="px-6 pt-4">
          <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full ${statusInfo.color}`}>
            <span>{statusInfo.icon}</span>
            {statusInfo.label}
          </span>
        </div>

        {/* QR o mensaje ya utilizado */}
        <div className="flex justify-center px-6 py-6">
          {isRedeemed ? (
            <div className="text-center">
              <p className="text-6xl mb-3">✔</p>
              <p className="text-slate-500 font-medium">Ya utilizada</p>
              {ticket.redeemedAt && (
                <p className="text-xs text-slate-400 mt-1">
                  {new Date(ticket.redeemedAt).toLocaleString("es-AR", {
                    dateStyle: "short", timeStyle: "short",
                  })}
                </p>
              )}
            </div>
          ) : (
            <div className="p-3 bg-white border border-slate-200 rounded-xl shadow-sm">
              <img
                src={qrImgUrl}
                width={200}
                height={200}
                alt="Código QR de la entrada"
                className="block"
              />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="px-6 pb-6 space-y-3">
          <InfoRow label="Titular" value={ticket.buyerName} />
          {dateStr && <InfoRow label="Fecha" value={dateStr} />}
          {event?.locationAddress && <InfoRow label="Lugar" value={event.locationAddress} />}
          {ticket.quantity > 1 && <InfoRow label="Cantidad" value={`${ticket.quantity} entradas`} />}
          <InfoRow label="Código" value={<code className="text-xs font-mono text-slate-500">{ticket.qrCode}</code>} />
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100">
          <p className="text-xs text-slate-400 text-center">
            Presentá este QR en la entrada del evento
          </p>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide min-w-[68px] pt-0.5">{label}</span>
      <span className="text-sm text-slate-700 font-medium">{value}</span>
    </div>
  );
}
